import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import ApartmentIcon from '@mui/icons-material/Apartment'
import ApiIcon from '@mui/icons-material/Api'
import AutoStoriesIcon from '@mui/icons-material/AutoStories'
import BoltIcon from '@mui/icons-material/Bolt'
import BusinessIcon from '@mui/icons-material/Business'
import CampaignIcon from '@mui/icons-material/Campaign'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import Co2Icon from '@mui/icons-material/Co2'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import ContactPhoneIcon from '@mui/icons-material/ContactPhone'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import EventIcon from '@mui/icons-material/Event'
import ExploreIcon from '@mui/icons-material/Explore'
import FeedbackIcon from '@mui/icons-material/Feedback'
import FlagIcon from '@mui/icons-material/Flag'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import HistoryIcon from '@mui/icons-material/History'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import HouseIcon from '@mui/icons-material/House'
import InboxIcon from '@mui/icons-material/Inbox'
import InsightsIcon from '@mui/icons-material/Insights'
import LeaderboardIcon from '@mui/icons-material/Leaderboard'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import PaymentsIcon from '@mui/icons-material/Payments'
import PublicIcon from '@mui/icons-material/Public'
import QuizIcon from '@mui/icons-material/Quiz'
import ReceiptIcon from '@mui/icons-material/Receipt'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RefreshIcon from '@mui/icons-material/Refresh'
import RuleIcon from '@mui/icons-material/Rule'
import SavingsIcon from '@mui/icons-material/Savings'
import ScheduleIcon from '@mui/icons-material/Schedule'
import SchoolIcon from '@mui/icons-material/School'
import ThermostatIcon from '@mui/icons-material/Thermostat'
import TimelineIcon from '@mui/icons-material/Timeline'
import TuneIcon from '@mui/icons-material/Tune'
import TvIcon from '@mui/icons-material/Tv'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { lazy, Suspense } from 'react'
import { matchPath, Navigate, useLocation, useParams } from 'react-router-dom'
import LoadingScreen from '@/components/LoadingScreen'
import { useHistoryNavBadge } from '@/features/dashboard/site-admin/meter-history/navBadge'
import { useFeedbackNavBadge } from '@/features/feedback/navBadge'
import ChatAltIcon from '@/icons/ChatAlt'
import ClipboardListIcon from '@/icons/ClipboardList'
import UsersIcon from '@/icons/Users'
import { isFettesHost } from '@/lib/host'
import {
    ADMIN_OR_PARTNER_ADMIN_ROLES,
    ADMIN_ROLES,
    FEEDBACK_ROLES,
    LOCATION_MEMBER_ROLES,
    MEMBER_ROLES,
    ORG_SETUP_ROLES,
    PARTNER_ADMIN_ROLES,
    PROPERTY_LEAGUE_ROLES,
    QUIZ_TEACHER_ROLES,
    SITE_ADMIN_ROLES,
    SLT_ROLES,
    STAFF_VIEW_ROLES
} from '@/lib/roles'

// Role constants live in @/lib/roles so route guards, homepage tiles and the
// feature registry all gate on the same arrays.

const Loadable = (Component) => (props) => (
    <Suspense fallback={<LoadingScreen />}>
        <Component {...props} />
    </Suspense>
)

const DashHome = Loadable(
    lazy(() => import('@/features/dashboard/home/DashHome'))
)
const Account = Loadable(lazy(() => import('@/pages/dashboard/Account')))
const PartnerConsole = Loadable(
    lazy(() => import('@/pages/dashboard/PartnerConsole'))
)
const OrgOnboarding = Loadable(
    lazy(() => import('@/pages/dashboard/onboarding/OrgOnboarding'))
)
const SetupHubPage = Loadable(
    lazy(() => import('@/pages/dashboard/setup/SetupHubPage'))
)
const SetupIntakePage = Loadable(
    lazy(() => import('@/pages/dashboard/setup/SetupIntakePage'))
)
const SiteAdminPartners = Loadable(
    lazy(() => import('@/pages/dashboard/site-admin/SiteAdminPartners'))
)
const SiteAdminGroups = Loadable(
    lazy(() => import('@/pages/dashboard/site-admin/SiteAdminGroups'))
)
const SiteAdminEducation = Loadable(
    lazy(() => import('@/pages/dashboard/site-admin/SiteAdminEducation'))
)
const GroupDashboard = Loadable(
    lazy(() => import('@/pages/dashboard/GroupDashboard'))
)
const NotificationsPage = Loadable(
    lazy(() => import('@/features/dashboard/notifications/NotificationsPage'))
)
const MessagingPage = Loadable(
    lazy(() =>
        import('@/pages/dashboard/messaging/MessagingPage').then((m) => ({
            default: m.MessagingPage
        }))
    )
)

const SiteAdmin = Loadable(
    lazy(() => import('@/pages/dashboard/site-admin/SiteAdmin'))
)
const SiteAdminMeterRefresh = Loadable(
    lazy(() => import('@/pages/dashboard/site-admin/SiteAdminMeterRefresh'))
)
const SiteAdminTasks = Loadable(
    lazy(() => import('@/pages/dashboard/site-admin/SiteAdminTasks'))
)
const SiteAdminOrgs = Loadable(
    lazy(() => import('@/pages/dashboard/site-admin/SiteAdminOrgs'))
)
const SiteAdminOrgCreate = Loadable(
    lazy(() => import('@/pages/dashboard/site-admin/SiteAdminOrgCreate'))
)
const SiteAdminOrgEdit = Loadable(
    lazy(() => import('@/pages/dashboard/site-admin/SiteAdminOrgEdit'))
)
const SiteAdminReports = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminReports').then((m) => ({
            default: m.SiteAdminReports
        }))
    )
)
const MyFeedbackPage = Loadable(
    lazy(() =>
        import('@/pages/dashboard/feedback/MyFeedbackPage').then((m) => ({
            default: m.MyFeedbackPage
        }))
    )
)
const SiteAdminFeatures = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminFeatures').then((m) => ({
            default: m.SiteAdminFeatures
        }))
    )
)
const SiteAdminFeedback = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminFeedback').then((m) => ({
            default: m.SiteAdminFeedback
        }))
    )
)
const SiteAdminUsers = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminUsers').then((m) => ({
            default: m.SiteAdminUsers
        }))
    )
)
const SiteAdminUserDetail = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminUserDetail').then(
            (m) => ({ default: m.SiteAdminUserDetail })
        )
    )
)
const SiteAdminAnnouncements = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminAnnouncements').then(
            (m) => ({ default: m.SiteAdminAnnouncements })
        )
    )
)
const SiteAdminAuditLog = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminAuditLog').then((m) => ({
            default: m.SiteAdminAuditLog
        }))
    )
)
const SiteAdminMeterFeeds = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminMeterFeeds').then(
            (m) => ({ default: m.SiteAdminMeterFeeds })
        )
    )
)
const SiteAdminMeterAccess = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminMeterAccess').then(
            (m) => ({ default: m.SiteAdminMeterAccess })
        )
    )
)
const SiteAdminMeterHistory = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminMeterHistory').then(
            (m) => ({ default: m.SiteAdminMeterHistory })
        )
    )
)
const SiteAdminFetchStatus = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminFetchStatus').then(
            (m) => ({
                default: m.SiteAdminFetchStatus
            })
        )
    )
)
const SiteAdminDataQuality = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminDataQuality').then(
            (m) => ({
                default: m.SiteAdminDataQuality
            })
        )
    )
)
const SiteAdminWeather = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminWeather').then((m) => ({
            default: m.SiteAdminWeather
        }))
    )
)
const SiteAdminCarbonFactors = Loadable(
    lazy(() =>
        import('@/pages/dashboard/site-admin/SiteAdminCarbonFactors').then(
            (m) => ({
                default: m.SiteAdminCarbonFactors
            })
        )
    )
)
const SiteAdminBilling = Loadable(
    lazy(() => import('@/features/dashboard/site-admin/billing'))
)

const AdminOverview = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminOverview'))
)
const AdminUsers = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminUsers'))
)
const AdminIsamsImportFailures = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminIsamsImportFailures'))
)
const AdminPowerFinance = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminPowerFinance'))
)
const AdminOccupancy = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminOccupancy'))
)
const AdminPowerBudgets = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminPowerBudgets'))
)
const AdminMeters = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminMeters'))
)
const AdminGasMeterEdit = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminGasMeterEdit'))
)
const AdminPowerMeterEdit = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminPowerMeterEdit'))
)
const AdminLocations = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminLocations'))
)
const AdminExternalServices = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminExternalServices'))
)
const AdminPropertyHub = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminPropertyHub'))
)
const AdminPupilGroups = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminPupilGroups'))
)
const TenantStatements = Loadable(
    lazy(() => import('@/pages/dashboard/tenant/TenantStatements'))
)
const AdminWaste = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminWaste'))
)
const AdminOrgEdit = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminOrgEdit'))
)
const AdminBilling = Loadable(
    lazy(() => import('@/features/dashboard/admin/billing'))
)
const AdminHolidays = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminHolidays'))
)
const AdminEquivalents = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminEquivalents'))
)
const AdminFetchStatus = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminFetchStatus'))
)
const AdminReadingImport = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminReadingImport'))
)
const AdminDataQuality = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AdminDataQuality'))
)
const PublicMeterOnlineStatus = Loadable(
    lazy(() => import('@/pages/public/MeterOnlineStatus'))
)
const AlertRules = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AlertRules'))
)
const AlertRuleForm = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AlertRuleForm'))
)
const AlertNotificationSettings = Loadable(
    lazy(() => import('@/pages/dashboard/admin/AlertNotificationSettings'))
)
const AlertsOverview = Loadable(
    lazy(() => import('@/pages/dashboard/alerts/AlertsOverview'))
)
const AlertDetail = Loadable(
    lazy(() => import('@/pages/dashboard/alerts/AlertDetail'))
)

const PowerOverview = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerOverview'))
)
const Timeline = Loadable(
    lazy(() => import('@/pages/dashboard/power/Timeline'))
)
const PowerHouses = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerHouses'))
)
const PowerPropertyLeague = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerPropertyLeague'))
)
const PowerHouseDetails = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerHouseDetails'))
)
const PowerExplore = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerExplore'))
)
const PowerInsights = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerInsights'))
)
const PowerClassProjects = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerClassProjects'))
)
const PowerCarbonReport = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerCarbonReport'))
)
const PowerAssistant = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerAssistant'))
)
const PowerTargets = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerTargets'))
)
const PowerActivities = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerActivities'))
)
const PowerActivityDetail = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerActivityDetail'))
)
const PartnerPortfolio = Loadable(
    lazy(() => import('@/pages/dashboard/PartnerPortfolio'))
)
const ReportsUnsubscribe = Loadable(
    lazy(() => import('@/pages/ReportsUnsubscribe'))
)
const EnergyQuiz = Loadable(
    lazy(() => import('@/pages/dashboard/quiz/EnergyQuiz'))
)
const QuizClassMode = Loadable(
    lazy(() => import('@/pages/dashboard/quiz/QuizClassMode'))
)
const PowerHallway = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerHallway'))
)
const QuizInsights = Loadable(
    lazy(() => import('@/pages/dashboard/quiz/QuizInsights'))
)
const PowerHolidays = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerHolidays'))
)
const PowerCompare = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerCompare'))
)
const PowerCompareDetails = Loadable(
    lazy(() => import('@/pages/dashboard/power/PowerCompareDetails'))
)

const WasteOverview = Loadable(
    lazy(() => import('@/pages/dashboard/waste/WasteOverview'))
)

const MarketingPage = Loadable(
    lazy(() =>
        import('@/pages/dashboard/marketing/Marketing').then((m) => ({
            default: m.MarketingPage
        }))
    )
)
const MarketingOrganizationsPage = Loadable(
    lazy(() =>
        import('@/pages/dashboard/marketing/MarketingOrganizations').then(
            (m) => ({ default: m.MarketingOrganizationsPage })
        )
    )
)
const MarketingOrganizationDetailPage = Loadable(
    lazy(() =>
        import('@/pages/dashboard/marketing/MarketingOrganizationDetail').then(
            (m) => ({ default: m.MarketingOrganizationDetailPage })
        )
    )
)
const MarketingContactsPage = Loadable(
    lazy(() =>
        import('@/pages/dashboard/marketing/MarketingContacts').then((m) => ({
            default: m.MarketingContactsPage
        }))
    )
)
const MarketingTemplatesPage = Loadable(
    lazy(() =>
        import('@/pages/dashboard/marketing/MarketingTemplates').then((m) => ({
            default: m.MarketingTemplatesPage
        }))
    )
)
const Pricing = Loadable(lazy(() => import('@/pages/Pricing')))
const ProjectCosts = Loadable(
    lazy(() => import('@/pages/dashboard/project-costs/ProjectCosts'))
)

// Location members (location-scoped-member-role) reach only the routes marked
// `locationScopeAware`, each adding these roles to its own guard. MEMBER_ROLES and
// SLT_ROLES are deliberately untouched: they also feed cost visibility and other routes.
const POWER_MEMBER_OR_LOCATION_ROLES = [
    ...MEMBER_ROLES,
    ...LOCATION_MEMBER_ROLES
]
const SLT_OR_LOCATION_ROLES = [...SLT_ROLES, ...LOCATION_MEMBER_ROLES]
const STAFF_VIEW_OR_LOCATION_ROLES = [
    ...STAFF_VIEW_ROLES,
    ...LOCATION_MEMBER_ROLES
]

const AdminPowerIndexRedirect = () => <Navigate to="/admin/meters" replace />

const AdminPowerMetersRedirect = () => <Navigate to="/admin/meters" replace />

// `/power/house/:meterId` was a second path to House Details. Old links and
// bookmarks still land on the page, with the meter (and any query) kept.
const PowerHouseRedirect = () => {
    const { meterId } = useParams()
    const { search, hash } = useLocation()
    return (
        <Navigate
            to={{
                pathname: `/power/house-details/${encodeURIComponent(meterId)}`,
                search,
                hash
            }}
            replace
        />
    )
}

const ROUTE_CONFIG = [
    {
        id: 'dashboard',
        subheader: 'Dashboard',
        routes: [
            {
                id: 'home',
                title: 'Home',
                href: '/',
                index: true,
                component: DashHome,
                // A location member's home is their Power overview.
                locationScopeRedirect: '/power',
                nav: {
                    surface: 'topLevel',
                    label: 'Home',
                    icon: <HomeOutlinedIcon />,
                    authRequired: true
                },
                bottomTab: {
                    label: 'Home',
                    icon: <HomeOutlinedIcon />,
                    authRequired: true,
                    order: 0
                }
            },
            {
                id: 'project-costs',
                title: 'Project costs',
                path: 'project-costs',
                href: '/project-costs',
                component: ProjectCosts,
                // Fettes' own project and invoice history: never shown to another org.
                fettesHostOnly: true,
                guard: 'RoleGuard',
                guardProps: { roles: SLT_ROLES },
                matchSubpaths: false,
                nav: {
                    surface: 'topLevel',
                    label: 'Project costs',
                    icon: <PaymentsIcon />,
                    authRequired: true,
                    roles: SLT_ROLES,
                    matchSubpaths: false
                }
            },
            {
                id: 'org-onboarding',
                title: 'Organisation setup',
                path: 'onboarding',
                href: '/onboarding',
                component: OrgOnboarding,
                guard: 'RoleGuard',
                guardProps: { roles: ORG_SETUP_ROLES },
                matchSubpaths: false
            },
            {
                id: 'setup-hub',
                title: 'Setup',
                path: 'setup',
                href: '/setup',
                component: SetupHubPage,
                guard: 'RoleGuard',
                guardProps: { roles: ORG_SETUP_ROLES },
                matchSubpaths: false
            },
            {
                // "What we need from you": the facts setup needs, asked per type of
                // organisation (onboarding/intake.py). Linked from the top of the setup hub.
                id: 'setup-intake',
                title: 'What we need from you',
                path: 'setup/intake',
                href: '/setup/intake',
                component: SetupIntakePage,
                guard: 'RoleGuard',
                guardProps: { roles: ORG_SETUP_ROLES },
                matchSubpaths: false
            },
            {
                // A tenant's own recharge statements (tenant-recharge-statements). Any member of
                // an estate org may open it; the API returns only statements on tenancies naming
                // the caller. The tenant email links here.
                id: 'tenant-statements',
                title: 'My statements',
                path: 'statements',
                href: '/statements',
                component: TenantStatements,
                requiresFlavour: ['estate'],
                requiresFeatureFlag: 'finance',
                // The Tenant location member's page: the API's my-statements views declare
                // location scope aware and narrow to the caller's own tenancies.
                locationScopeAware: true,
                nav: {
                    surface: 'settings',
                    label: 'My statements',
                    icon: <ReceiptLongIcon />,
                    authRequired: true
                }
            },
            {
                // A staff member's own page feedback and our replies. The reply
                // notification links here; the API's mine/ view is self-scoped.
                id: 'my-feedback',
                title: 'My feedback',
                path: 'feedback',
                href: '/feedback',
                component: MyFeedbackPage,
                guard: 'RoleGuard',
                guardProps: { roles: FEEDBACK_ROLES },
                // The `page_feedback` feature flag: off hides the entry and the
                // page (the API's submit and mine/ answer 404 too; the site-admin
                // inbox stays open).
                requiresPlatformFeature: 'page_feedback',
                locationScopeAware: true,
                nav: {
                    surface: 'settings',
                    label: 'My feedback',
                    icon: <FeedbackIcon />,
                    authRequired: true,
                    roles: FEEDBACK_ROLES
                }
            },
            {
                id: 'account',
                title: 'Account',
                path: 'account/*',
                href: '/account',
                component: Account,
                // The member's own account (the API's self-scoped views).
                locationScopeAware: true,
                nav: {
                    surface: 'settings',
                    label: 'User',
                    icon: <ManageAccountsIcon />,
                    authRequired: true
                },
                bottomTab: {
                    label: 'Account',
                    icon: <ManageAccountsIcon />,
                    authRequired: true,
                    order: 2
                }
            },
            {
                id: 'messaging',
                title: 'Messages',
                path: 'messaging',
                href: '/messaging',
                component: MessagingPage
                // nav: {
                //     surface: 'topLevel',
                //     label: 'Messages',
                //     icon: <ForumOutlinedIcon />,
                //     authRequired: true
                // }
            },
            {
                // Reached from the notifications bell's "View all".
                id: 'notifications',
                title: 'Notifications',
                path: 'notifications',
                href: '/notifications',
                component: NotificationsPage
            },
            {
                id: 'messaging-dm',
                title: 'Messages',
                path: 'messaging/dm/:userId',
                href: '/messaging/dm/:userId',
                component: MessagingPage
            },
            {
                id: 'pricing',
                title: 'Pricing',
                path: 'pricing',
                href: '/pricing',
                component: Pricing,
                public: true,
                matchSubpaths: false
            },
            {
                id: 'public-meteronline-status',
                title: 'MeterOnline Fetch Status',
                path: 'public/meteronline-status',
                href: '/public/meteronline-status',
                component: PublicMeterOnlineStatus,
                public: true,
                matchSubpaths: false
            },
            {
                id: 'reports-unsubscribe',
                title: 'Unsubscribe from reports',
                path: 'reports/unsubscribe',
                href: '/reports/unsubscribe',
                component: ReportsUnsubscribe,
                public: true,
                matchSubpaths: false
            }
        ]
    },
    {
        id: 'partner',
        subheader: 'Partner',
        roles: PARTNER_ADMIN_ROLES,
        navSection: {
            id: 'partner',
            label: 'Partner',
            icon: <BusinessIcon />,
            pinToTop: true,
            authRequired: true,
            roles: PARTNER_ADMIN_ROLES
        },
        routes: [
            {
                id: 'partner-portfolio',
                title: 'Portfolio',
                path: 'partner/portfolio',
                href: '/partner/portfolio',
                component: PartnerPortfolio,
                guard: 'RoleGuard',
                guardProps: { roles: PARTNER_ADMIN_ROLES },
                requiresFeatureFlag: 'portfolio',
                matchSubpaths: false
            },
            {
                id: 'partner-console',
                title: 'Partner console',
                path: 'partner',
                href: '/partner',
                component: PartnerConsole,
                guard: 'RoleGuard',
                guardProps: { roles: PARTNER_ADMIN_ROLES },
                matchSubpaths: false,
                nav: {
                    surface: 'section',
                    label: 'Console',
                    icon: <BusinessIcon />,
                    matchSubpaths: false
                }
            }
        ]
    },
    {
        // MAT / LA group dashboard. Visible only to users the API reports as group
        // admins (administeredGroups on the me payload) — not role-gated: designation
        // lives in the group's admins M2M, surfaced via requiresGroupAdmin.
        id: 'group',
        subheader: 'Group',
        navSection: {
            id: 'group',
            label: 'Group',
            icon: <ApartmentIcon />,
            pinToTop: true,
            authRequired: true,
            requiresGroupAdmin: true
        },
        routes: [
            {
                id: 'group-dashboard',
                title: 'Group dashboard',
                path: 'group-dashboard',
                href: '/group-dashboard',
                component: GroupDashboard,
                requiresGroupAdmin: true,
                matchSubpaths: false,
                nav: {
                    surface: 'section',
                    label: 'Dashboard',
                    icon: <ApartmentIcon />,
                    requiresGroupAdmin: true,
                    matchSubpaths: false
                }
            }
        ]
    },
    {
        id: 'site-admin',
        subheader: 'Site Admin',
        roles: SITE_ADMIN_ROLES,
        // Like Admin: one entry in the main sidebar, and every page inside it
        // reached through the shell's grouped nav. Routes with
        // `nav.surface: 'site-admin'` are placed by `SITE_ADMIN_GROUPS`
        // (navConfig.jsx).
        layout: 'SiteAdminLayout',
        routes: [
            {
                id: 'site-admin-overview',
                title: 'Site Admin',
                path: 'site-admin',
                href: '/site-admin',
                component: SiteAdmin,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                matchSubpaths: false,
                nav: {
                    surface: 'topLevel',
                    label: 'Site admin',
                    icon: <PublicIcon />,
                    authRequired: true,
                    roles: SITE_ADMIN_ROLES,
                    // Stay highlighted on every /site-admin/* page.
                    matchSubpaths: true
                }
            },
            {
                id: 'site-admin-partners',
                title: 'Partners',
                path: 'site-admin/partners',
                href: '/site-admin/partners',
                component: SiteAdminPartners,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Partners',
                    description:
                        'Resellers and white-label partners, their branding and the organisations they manage.',
                    icon: <BusinessIcon />
                }
            },
            {
                id: 'site-admin-groups',
                title: 'Groups',
                path: 'site-admin/groups',
                href: '/site-admin/groups',
                component: SiteAdminGroups,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Groups',
                    description:
                        'Groups of organisations, such as trusts or portfolios, and who administers them.',
                    icon: <ApartmentIcon />
                }
            },
            {
                id: 'site-admin-education',
                title: 'Education content',
                path: 'site-admin/education',
                href: '/site-admin/education',
                component: SiteAdminEducation,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Education content',
                    description:
                        'Articles and activities in the education library, and their categories.',
                    icon: <AutoStoriesIcon />
                }
            },
            {
                id: 'site-admin-announcements',
                title: 'Announcements',
                path: 'site-admin/announcements',
                href: '/site-admin/announcements',
                component: SiteAdminAnnouncements,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Announcements',
                    description:
                        'Send a message to users across the platform, and see past announcements.',
                    icon: <CampaignIcon />
                }
            },
            {
                id: 'site-admin-audit-log',
                title: 'Audit log',
                path: 'site-admin/audit-log',
                href: '/site-admin/audit-log',
                component: SiteAdminAuditLog,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Audit log',
                    description:
                        'Who did what and when, filterable by admin, action and date.',
                    icon: <ClipboardListIcon />
                }
            },
            {
                id: 'site-admin-fetch-status',
                title: 'Fetch status',
                path: 'site-admin/fetch-status',
                href: '/site-admin/fetch-status',
                component: SiteAdminFetchStatus,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Fetch status',
                    description:
                        'The latest reading fetch for every organisation and provider, and what is failing.',
                    icon: <CloudDownloadIcon />
                }
            },
            {
                id: 'site-admin-meter-feeds',
                title: 'Data feeds',
                path: 'site-admin/meter-feeds',
                href: '/site-admin/meter-feeds',
                component: SiteAdminMeterFeeds,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Data feeds',
                    description:
                        'Suppliers and data collectors that send half-hourly files, and every file received.',
                    icon: <InboxIcon />
                }
            },
            {
                id: 'site-admin-meter-access',
                title: 'Meter access requests',
                path: 'site-admin/meter-access',
                href: '/site-admin/meter-access',
                component: SiteAdminMeterAccess,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Meter access requests',
                    description:
                        'Bills and Letters of Authority from organisations, to check before Power collects their meters.',
                    icon: <VerifiedUserIcon />
                }
            },
            {
                id: 'site-admin-meter-history',
                title: 'Past data requests',
                path: 'site-admin/meter-history',
                href: '/site-admin/meter-history',
                component: SiteAdminMeterHistory,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Past data requests',
                    description:
                        "Requests to suppliers for organisations' past year of half-hourly data, and how each supplier answers.",
                    icon: <HistoryIcon />,
                    useBadgeCount: useHistoryNavBadge,
                    badgeLabel: 'to send or chase'
                }
            },
            {
                id: 'site-admin-data-quality',
                title: 'Data quality',
                path: 'site-admin/data-quality',
                href: '/site-admin/data-quality',
                component: SiteAdminDataQuality,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Data quality',
                    description:
                        'Suspicious or missing readings found across all organisations.',
                    icon: <RuleIcon />
                }
            },
            {
                id: 'site-admin-weather',
                title: 'Weather',
                path: 'site-admin/weather',
                href: '/site-admin/weather',
                component: SiteAdminWeather,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Weather',
                    description:
                        'The weather history used to adjust heating figures, and refreshing or backfilling it.',
                    icon: <ThermostatIcon />
                }
            },
            {
                id: 'site-admin-carbon-factors',
                title: 'Carbon factors',
                path: 'site-admin/carbon-factors',
                href: '/site-admin/carbon-factors',
                component: SiteAdminCarbonFactors,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Carbon factors',
                    description:
                        "Government carbon conversion factors by year; upload a new year's workbook here.",
                    icon: <Co2Icon />
                }
            },
            {
                id: 'site-admin-reports',
                title: 'Content reports',
                path: 'site-admin/reports',
                href: '/site-admin/reports',
                component: SiteAdminReports,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Content reports',
                    description:
                        'Posts and content users have reported, waiting for review.',
                    icon: <FlagIcon />
                }
            },
            {
                id: 'site-admin-feedback',
                title: 'Page feedback',
                path: 'site-admin/feedback',
                href: '/site-admin/feedback',
                component: SiteAdminFeedback,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                // Deliberately not behind `page_feedback`: with the flag off no new
                // feedback arrives, but site admins can still read, resolve and
                // reply to what is there (the inbox says it is switched off).
                nav: {
                    surface: 'site-admin',
                    label: 'Page feedback',
                    description:
                        'Comments and ratings staff have left on pages.',
                    icon: <FeedbackIcon />,
                    useBadgeCount: useFeedbackNavBadge,
                    badgeLabel: 'open'
                }
            },
            {
                // Platform feature flags (backend core/feature_flags.py): switches for
                // every organisation at once. Not organisation features, which are
                // switched on the organisation's edit page (orgFeatureFlags.js).
                id: 'site-admin-features',
                title: 'Features',
                path: 'site-admin/features',
                href: '/site-admin/features',
                component: SiteAdminFeatures,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Features',
                    description:
                        'Switch features on or off for every organisation at once, with a reason and a history.',
                    icon: <TuneIcon />
                }
            },
            {
                id: 'site-admin-meter-refresh',
                title: 'Meter data refresh',
                path: 'site-admin/meter-refresh',
                href: '/site-admin/meter-refresh',
                component: SiteAdminMeterRefresh,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Meter data refresh',
                    description:
                        'Re-fetch smart meter readings for chosen meters and dates.',
                    icon: <RefreshIcon />
                }
            },
            {
                id: 'site-admin-orgs',
                title: 'Organisations',
                path: 'site-admin/orgs',
                href: '/site-admin/orgs',
                component: SiteAdminOrgs,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Organisations',
                    description:
                        'Create and edit customer organisations, and switch their features on or off.',
                    icon: <BusinessIcon />
                }
            },
            {
                id: 'site-admin-org-create',
                title: 'Create organisation',
                path: 'site-admin/orgs/create',
                href: '/site-admin/orgs/create',
                component: SiteAdminOrgCreate,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES }
            },
            {
                id: 'site-admin-org-edit',
                title: 'Edit organisation',
                path: 'site-admin/orgs/:orgId/edit',
                href: '/site-admin/orgs/:orgId/edit',
                component: SiteAdminOrgEdit,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES }
            },
            {
                id: 'site-admin-tasks',
                title: 'Background tasks',
                path: 'site-admin/tasks',
                href: '/site-admin/tasks',
                component: SiteAdminTasks,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Background tasks',
                    description:
                        'Exports, imports, demo seeding and other one-off jobs for the active organisation.',
                    icon: <ApiIcon />
                }
            },
            {
                id: 'site-admin-users',
                title: 'Users',
                path: 'site-admin/users',
                href: '/site-admin/users',
                component: SiteAdminUsers,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Users',
                    description:
                        'Find anyone on the platform, and see their roles and organisation.',
                    icon: <UsersIcon />
                }
            },
            {
                id: 'site-admin-user-detail',
                title: 'User details',
                path: 'site-admin/users/:id',
                href: '/site-admin/users/:id',
                component: SiteAdminUserDetail,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES }
            },
            {
                id: 'site-admin-billing',
                title: 'Billing',
                path: 'site-admin/billing',
                href: '/site-admin/billing',
                component: SiteAdminBilling,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'site-admin',
                    label: 'Billing',
                    description:
                        'Subscriptions, invoices and payment status for every organisation.',
                    icon: <ReceiptIcon />
                }
            }
        ]
    },
    {
        id: 'admin',
        subheader: 'Admin',
        roles: ADMIN_ROLES,
        // Admin gets one entry in the main sidebar; every page inside it is
        // reached through AdminLayout's own grouped nav. Routes with
        // `nav.surface: 'admin'` appear there and on the Admin overview, in
        // the categories and order `ADMIN_GROUPS` (navConfig.jsx) gives them,
        // each with its `nav.description` saying what the page is for.
        layout: 'AdminLayout',
        routes: [
            {
                id: 'admin-overview',
                title: 'Admin',
                path: 'admin',
                href: '/admin',
                component: AdminOverview,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_ROLES },
                matchSubpaths: false,
                nav: {
                    surface: 'topLevel',
                    label: 'Admin',
                    icon: <AdminPanelSettingsIcon />,
                    authRequired: true,
                    roles: ADMIN_ROLES,
                    // Stay highlighted on every /admin/* page.
                    matchSubpaths: true
                }
            },
            {
                id: 'admin-org-edit',
                title: 'Organisation Settings',
                path: 'admin/org/edit',
                href: '/admin/org/edit',
                component: AdminOrgEdit,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Organisation settings',
                    description:
                        "Your organisation's name, logo and contact details.",
                    icon: <BusinessIcon />
                }
            },
            {
                id: 'admin-billing',
                title: 'Billing',
                path: 'admin/billing',
                href: '/admin/billing',
                component: AdminBilling,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_ROLES },
                requiresFeatureFlag: 'billing_self_service',
                nav: {
                    surface: 'admin',
                    label: 'Billing',
                    description: 'Your plan, invoices and payment details.',
                    icon: <ReceiptIcon />
                }
            },
            {
                id: 'admin-external-services',
                title: 'External Services',
                path: 'admin/external-services',
                href: '/admin/external-services',
                component: AdminExternalServices,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'External services',
                    description:
                        'Connect utility providers, single sign-on and your people directory.',
                    icon: <ApiIcon />
                }
            },
            {
                id: 'admin-property-hub',
                title: 'Properties',
                path: 'admin/properties',
                href: '/admin/properties',
                component: AdminPropertyHub,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                requiresFlavour: ['estate'],
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Properties',
                    description:
                        "Each property's details, connection, schedule and tariff in one place.",
                    icon: <ApartmentIcon />
                }
            },
            {
                id: 'admin-fetch-status',
                title: 'Fetch status',
                path: 'admin/fetch-status',
                href: '/admin/fetch-status',
                component: AdminFetchStatus,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    label: 'Fetch status',
                    description:
                        'When readings were last collected from each provider, and any gaps.',
                    icon: <CloudDownloadIcon />
                }
            },
            {
                id: 'admin-reading-import',
                title: 'Import readings',
                path: 'admin/reading-import',
                href: '/admin/reading-import',
                component: AdminReadingImport,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Import readings',
                    description:
                        'Upload readings from a spreadsheet for meters without a live feed.',
                    icon: <CloudUploadIcon />
                }
            },
            {
                id: 'admin-data-quality',
                title: 'Data quality',
                path: 'admin/data-quality',
                href: '/admin/data-quality',
                component: AdminDataQuality,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Data quality',
                    description:
                        'Readings that look wrong or are missing, and how to fix them.',
                    icon: <RuleIcon />
                }
            },
            {
                id: 'admin-holidays',
                title: 'Holidays',
                termKey: 'holidaysTitle',
                path: 'admin/holidays',
                href: '/admin/holidays',
                component: AdminHolidays,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                requiresFeatureFlag: 'holidays',
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Holidays',
                    descriptionTermKey: 'adminHolidaysDescription',
                    icon: <EventIcon />
                }
            },
            {
                id: 'admin-alert-rules',
                title: 'Alert Rules',
                path: 'admin/alerts/rules',
                href: '/admin/alerts/rules',
                component: AlertRules,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                matchSubpaths: false,
                requiresFeatureFlag: 'alerts',
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Alert rules',
                    description:
                        'What counts as unusual use, and when an alert is raised.',
                    icon: <RuleIcon />
                }
            },
            {
                id: 'admin-alert-rule-new',
                title: 'New Alert Rule',
                path: 'admin/alerts/rules/new',
                href: '/admin/alerts/rules/new',
                component: AlertRuleForm,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                requiresFeatureFlag: 'alerts'
            },
            {
                id: 'admin-alert-rule-edit',
                title: 'Edit Alert Rule',
                path: 'admin/alerts/rules/:ruleId/edit',
                href: '/admin/alerts/rules/:ruleId/edit',
                component: AlertRuleForm,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                requiresFeatureFlag: 'alerts'
            },
            {
                id: 'admin-alert-notifications',
                title: 'Alert Notifications',
                path: 'admin/alerts/notifications',
                href: '/admin/alerts/notifications',
                component: AlertNotificationSettings,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_ROLES },
                requiresFeatureFlag: 'alerts',
                nav: {
                    surface: 'admin',
                    label: 'Alert notifications',
                    description:
                        'Who receives alert emails, and pausing delivery.',
                    icon: <CampaignIcon />
                }
            },
            {
                id: 'admin-equivalents',
                title: 'Power Equivalents',
                path: 'admin/equivalents',
                href: '/admin/equivalents',
                component: AdminEquivalents,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    label: 'Power equivalents',
                    description:
                        'The everyday comparisons that make energy figures relatable.',
                    icon: <CompareArrowsIcon />
                }
            },
            {
                id: 'admin-finance',
                title: 'Finance',
                path: 'admin/finance',
                href: '/admin/finance',
                component: AdminPowerFinance,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                requiresFeatureFlag: 'finance',
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Finance',
                    description:
                        'Tariffs, supply contracts, bills and statements.',
                    icon: <PaymentsIcon />
                }
            },
            {
                id: 'admin-occupancy',
                title: 'Occupancy Schedule',
                path: 'admin/occupancy',
                href: '/admin/occupancy',
                component: AdminOccupancy,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                requiresFeatureFlag: 'occupancy',
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Occupancy schedule',
                    description:
                        'The hours your buildings are in use, so out-of-hours use stands out.',
                    icon: <ScheduleIcon />
                }
            },
            {
                id: 'admin-budgets',
                title: 'Budgets',
                path: 'admin/budgets',
                href: '/admin/budgets',
                component: AdminPowerBudgets,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                requiresFeatureFlag: 'budgets',
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Budgets',
                    description:
                        'Spending and usage budgets, tracked against actuals.',
                    icon: <SavingsIcon />
                }
            },
            {
                id: 'admin-locations',
                title: 'Locations',
                path: 'admin/locations',
                href: '/admin/locations',
                component: AdminLocations,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Locations',
                    description:
                        'The buildings and areas you monitor, and where they sit on the map.',
                    icon: <HouseIcon />
                }
            },
            {
                id: 'admin-power-index',
                title: 'Admin Power',
                path: 'admin/power',
                href: '/admin/power',
                component: AdminPowerIndexRedirect,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES }
            },
            {
                id: 'admin-power-meters-redirect',
                title: 'Meters',
                path: 'admin/power/meters',
                href: '/admin/power/meters',
                component: AdminPowerMetersRedirect,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES }
            },
            {
                id: 'admin-meters',
                title: 'Meters',
                path: 'admin/meters',
                href: '/admin/meters',
                component: AdminMeters,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Meters',
                    // A meter's edit page lives under the old per-fuel paths.
                    activePaths: ['/admin/power/meters', '/admin/gas/meters'],
                    description:
                        'Electricity, gas and water meters, and the location each one serves.',
                    icon: <BoltIcon />
                }
            },
            {
                id: 'admin-gas-meter-edit',
                title: 'Edit gas meter',
                path: 'admin/gas/meters/:meterId/edit',
                href: '/admin/gas/meters/:meterId/edit',
                component: AdminGasMeterEdit,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES }
            },
            {
                id: 'admin-power-meter-edit',
                title: 'Edit electricity meter',
                path: 'admin/power/meters/:meterId/edit',
                href: '/admin/power/meters/:meterId/edit',
                component: AdminPowerMeterEdit,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES }
            },
            {
                id: 'admin-users',
                title: 'Users & roles',
                path: 'admin/users',
                href: '/admin/users',
                component: AdminUsers,
                guard: 'RoleGuard',
                // Partner admins manage the people in their own portfolio's orgs (the
                // setup hub's "invite your team" step links here). The staff-admin users
                // API admits them and scopes them to their partner's orgs.
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'Users & roles',
                    description:
                        'Invite people, and choose what each person can see and change.',
                    icon: <UsersIcon />
                }
            },
            {
                id: 'admin-pupil-groups',
                title: 'Houses & groups',
                termKey: 'groups',
                path: 'admin/pupil-groups',
                href: '/admin/pupil-groups',
                component: AdminPupilGroups,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_ROLES },
                requiresFlavour: ['school', 'university'],
                requiresFeatureFlag: 'houses',
                nav: {
                    surface: 'admin',
                    label: 'Houses & groups',
                    description:
                        'Houses and other groups, and the buildings they own.',
                    icon: <HouseIcon />
                }
            },
            {
                id: 'admin-isams-import-failures',
                title: 'MIS Import Failures',
                path: 'admin/isams-import-failures',
                href: '/admin/isams-import-failures',
                component: AdminIsamsImportFailures,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_OR_PARTNER_ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    roles: ADMIN_OR_PARTNER_ADMIN_ROLES,
                    label: 'MIS import failures',
                    description:
                        'People the nightly directory import skipped, and why.',
                    icon: <ErrorOutlineIcon />
                }
            },
            {
                id: 'admin-waste',
                title: 'Waste Updates',
                path: 'admin/waste',
                href: '/admin/waste',
                component: AdminWaste,
                guard: 'RoleGuard',
                guardProps: { roles: ADMIN_ROLES },
                nav: {
                    surface: 'admin',
                    label: 'Waste & recycling',
                    description: 'Monthly waste and recycling figures.',
                    icon: <DeleteOutlineIcon />
                }
            }
        ]
    },
    {
        id: 'power',
        subheader: 'Power',
        roles: POWER_MEMBER_OR_LOCATION_ROLES,
        navSection: {
            id: 'power',
            label: 'Power',
            icon: <BoltIcon />,
            authRequired: true,
            roles: POWER_MEMBER_OR_LOCATION_ROLES,
            locationScopeAware: true,
            // Sidebar order comes from the feature registry's `order`, the same
            // field that positions the homepage tiles — see orgFeatureFlags.js.
            orderedByFeatureRegistry: true
        },
        routes: [
            {
                id: 'power-overview',
                title: 'Power overview',
                path: 'power',
                href: '/power',
                component: PowerOverview,
                guard: 'RoleGuard',
                guardProps: { roles: POWER_MEMBER_OR_LOCATION_ROLES },
                locationScopeAware: true,
                matchSubpaths: false,
                nav: {
                    surface: 'section',
                    label: 'Overview',
                    icon: <BoltIcon />,
                    matchSubpaths: false,
                    // Always on, so it has no feature flag — name its registry
                    // entry explicitly to take a sidebar position.
                    featureKey: 'overview'
                },
                // The energy dashboard is the app's core destination; without
                // this a phone user needed More -> Power -> Overview. It is the
                // Power section's one always-on route (no feature flag, no
                // billing gate), so the tab needs no gating beyond the section's
                // MEMBER_ROLES, which bottomNavItems inherits via sectionNav.
                // The tab stays lit across every /power/* page.
                bottomTab: {
                    label: 'Power',
                    icon: <BoltIcon />,
                    authRequired: true,
                    order: 1
                }
            },
            {
                id: 'power-timeline',
                title: 'Timeline',
                path: 'power/timeline',
                href: '/power/timeline',
                component: Timeline,
                guard: 'RoleGuard',
                guardProps: { roles: MEMBER_ROLES },
                requiresFeatureFlag: 'timeline',
                nav: {
                    surface: 'section',
                    label: 'Timeline',
                    icon: <TimelineIcon />
                }
            },
            {
                id: 'power-houses',
                title: 'Houses Summary',
                termKey: 'housesSummaryTitle',
                path: 'power/houses',
                href: '/power/houses',
                component: PowerHouses,
                guard: 'RoleGuard',
                guardProps: { roles: MEMBER_ROLES },
                requiresFeatureFlag: 'houses',
                nav: {
                    surface: 'section',
                    label: 'Houses Summary',
                    icon: <HouseIcon />
                }
            },
            {
                // The intra-org property league (property-league-and-house-
                // standings): read-only for the org's staff, estate and business
                // only. Same `houses` flag as the Summary charts it ranks, so it
                // sorts next to them in the registry-ordered section.
                id: 'power-property-league',
                title: 'Properties league',
                termKey: 'housesLeagueTitle',
                path: 'power/league',
                href: '/power/league',
                component: PowerPropertyLeague,
                guard: 'RoleGuard',
                guardProps: { roles: PROPERTY_LEAGUE_ROLES },
                requiresFlavour: ['estate', 'business'],
                requiresFeatureFlag: 'houses',
                nav: {
                    surface: 'section',
                    label: 'Properties league',
                    icon: <LeaderboardIcon />,
                    roles: PROPERTY_LEAGUE_ROLES
                }
            },
            {
                id: 'power-house-details',
                title: 'House Details',
                termKey: 'houseDetailsTitle',
                path: 'power/house-details',
                href: '/power/house-details',
                component: PowerHouseDetails,
                guard: 'RoleGuard',
                guardProps: { roles: SLT_ROLES },
                requiresFeatureFlag: 'house_details',
                nav: {
                    surface: 'section',
                    label: 'House Details',
                    icon: <HouseIcon />,
                    roles: SLT_ROLES
                }
            },
            {
                id: 'power-house-details-id',
                title: 'House Details',
                termKey: 'houseDetailsTitle',
                path: 'power/house-details/:meterId',
                href: '/power/house-details/:meterId',
                component: PowerHouseDetails,
                guard: 'RoleGuard',
                guardProps: { roles: SLT_ROLES },
                requiresFeatureFlag: 'house_details'
            },
            {
                id: 'power-house-redirect',
                title: 'House Details',
                termKey: 'houseDetailsTitle',
                path: 'power/house/:meterId',
                href: '/power/house/:meterId',
                component: PowerHouseRedirect,
                guard: 'RoleGuard',
                guardProps: { roles: SLT_ROLES },
                requiresFeatureFlag: 'house_details'
            },
            {
                id: 'power-explore',
                title: 'Explore Data',
                path: 'power/explore',
                href: '/power/explore',
                component: PowerExplore,
                guard: 'RoleGuard',
                guardProps: { roles: SLT_OR_LOCATION_ROLES },
                locationScopeAware: true,
                requiresFeatureFlag: 'explore',
                nav: {
                    surface: 'section',
                    label: 'Explore Data',
                    icon: <ExploreIcon />,
                    roles: SLT_OR_LOCATION_ROLES
                }
            },
            {
                id: 'power-insights',
                title: 'Insights',
                path: 'power/insights',
                href: '/power/insights',
                component: PowerInsights,
                guard: 'RoleGuard',
                guardProps: { roles: SLT_OR_LOCATION_ROLES },
                locationScopeAware: true,
                requiresBilling: true,
                requiresFeatureFlag: 'insights',
                nav: {
                    surface: 'section',
                    label: 'Insights',
                    icon: <InsightsIcon />,
                    roles: SLT_OR_LOCATION_ROLES
                }
            },
            {
                id: 'power-class-projects',
                title: 'Class Projects',
                termKey: 'classProjectsTitle',
                path: 'power/class-projects',
                href: '/power/class-projects',
                component: PowerClassProjects,
                guard: 'RoleGuard',
                guardProps: { roles: MEMBER_ROLES },
                requiresFeatureFlag: 'class_projects',
                nav: {
                    surface: 'section',
                    label: 'Class Projects',
                    icon: <SchoolIcon />
                }
            },
            {
                id: 'power-activities',
                title: 'Activities',
                termKey: 'activitiesTitle',
                path: 'power/activities',
                href: '/power/activities',
                component: PowerActivities,
                guard: 'RoleGuard',
                guardProps: { roles: MEMBER_ROLES },
                requiresFeatureFlag: 'education',
                nav: {
                    surface: 'section',
                    label: 'Activities',
                    termKey: 'activitiesTitle',
                    icon: <AutoStoriesIcon />
                }
            },
            {
                id: 'power-activity-detail',
                title: 'Activities',
                termKey: 'activitiesTitle',
                path: 'power/activities/:slug',
                href: '/power/activities/:slug',
                component: PowerActivityDetail,
                guard: 'RoleGuard',
                guardProps: { roles: MEMBER_ROLES },
                requiresFeatureFlag: 'education'
            },
            {
                id: 'power-carbon-report',
                title: 'Carbon Report',
                path: 'power/carbon-report',
                href: '/power/carbon-report',
                component: PowerCarbonReport,
                guard: 'RoleGuard',
                guardProps: { roles: MEMBER_ROLES },
                requiresFeatureFlag: 'carbon_reporting',
                nav: {
                    surface: 'section',
                    label: 'Carbon Report',
                    icon: <Co2Icon />
                }
            },
            {
                id: 'power-assistant',
                title: 'Assistant',
                path: 'power/assistant',
                href: '/power/assistant',
                component: PowerAssistant,
                // BOTH gates, matching the backend exactly: the `assistant`
                // feature flag AND site admin. The role restriction is
                // scaffolding that comes out when real users arrive; the flag
                // is permanent. The endpoints enforce both independently, so
                // this guard is convenience rather than the control.
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                requiresFeatureFlag: 'assistant',
                nav: {
                    surface: 'section',
                    label: 'Assistant',
                    icon: <ChatAltIcon />
                }
            },
            {
                id: 'power-targets',
                title: 'Reduction Targets',
                termKey: 'targetsTitle',
                path: 'power/targets',
                href: '/power/targets',
                component: PowerTargets,
                guard: 'RoleGuard',
                guardProps: { roles: MEMBER_ROLES },
                requiresBilling: true,
                requiresFeatureFlag: 'targets',
                nav: {
                    surface: 'section',
                    label: 'Reduction Targets',
                    termKey: 'targetsTitle',
                    icon: <FlagIcon />
                }
            },
            {
                id: 'power-quiz',
                title: 'Energy Quiz',
                termKey: 'energyQuizTitle',
                path: 'power/quiz',
                href: '/power/quiz',
                component: EnergyQuiz,
                guard: 'RoleGuard',
                guardProps: { roles: MEMBER_ROLES },
                requiresFeatureFlag: 'energy_quiz',
                nav: {
                    surface: 'section',
                    label: 'Energy Quiz',
                    icon: <QuizIcon />
                }
            },
            // Teacher-only quiz sub-pages, reached from buttons on the quiz
            // page rather than the nav (the Energy Quiz entry stays
            // highlighted for them, as it matches sub-paths). Same flag as the
            // quiz; roles match the backend's can_teach check.
            {
                id: 'power-quiz-class',
                title: 'Class mode',
                path: 'power/quiz/class',
                href: '/power/quiz/class',
                component: QuizClassMode,
                guard: 'RoleGuard',
                guardProps: { roles: QUIZ_TEACHER_ROLES },
                requiresFeatureFlag: 'energy_quiz'
            },
            {
                id: 'power-quiz-insights',
                title: 'Teacher insights',
                path: 'power/quiz/insights',
                href: '/power/quiz/insights',
                component: QuizInsights,
                guard: 'RoleGuard',
                guardProps: { roles: QUIZ_TEACHER_ROLES },
                requiresFeatureFlag: 'energy_quiz'
            },
            // The school TV display (staff open it, everyone reads it). Shows only what the
            // player-safe APIs return, so it needs no feature flag of its own.
            {
                id: 'power-hallway',
                title: 'Hallway screen',
                path: 'power/hallway',
                href: '/power/hallway',
                component: PowerHallway,
                guard: 'RoleGuard',
                guardProps: { roles: QUIZ_TEACHER_ROLES },
                requiresFlavour: ['school'],
                nav: {
                    surface: 'section',
                    label: 'Hallway screen',
                    icon: <TvIcon />,
                    roles: QUIZ_TEACHER_ROLES
                }
            },
            {
                id: 'power-holidays',
                title: 'Holidays',
                termKey: 'holidaysTitle',
                path: 'power/holidays',
                href: '/power/holidays',
                component: PowerHolidays,
                guard: 'RoleGuard',
                guardProps: { roles: SLT_OR_LOCATION_ROLES },
                locationScopeAware: true,
                requiresBilling: true,
                requiresFeatureFlag: 'holidays',
                nav: {
                    surface: 'section',
                    label: 'Holidays',
                    icon: <EventIcon />,
                    roles: SLT_OR_LOCATION_ROLES
                }
            }
            // {
            //     id: 'power-comparisons',
            //     title: 'Comparisons',
            //     path: 'power/comparisons',
            //     href: '/power/comparisons',
            //     component: PowerCompare,
            //     guard: 'RoleGuard',
            //     guardProps: { roles: MEMBER_ROLES },
            //     nav: {
            //         surface: 'section',
            //         label: 'Comparisons',
            //         icon: <CompareArrowsIcon />
            //     }
            // },
            // {
            //     id: 'power-comparisons-detail',
            //     title: 'Comparison Details',
            //     path: 'power/comparisons/:comparisonId',
            //     href: '/power/comparisons/:comparisonId',
            //     component: PowerCompareDetails,
            //     guard: 'RoleGuard',
            //     guardProps: { roles: MEMBER_ROLES }
            // }
        ]
    },
    {
        id: 'alerts',
        subheader: 'Alerts',
        roles: STAFF_VIEW_OR_LOCATION_ROLES,
        routes: [
            {
                id: 'alerts-overview',
                title: 'Alerts',
                path: 'alerts',
                href: '/alerts',
                component: AlertsOverview,
                guard: 'RoleGuard',
                guardProps: { roles: STAFF_VIEW_OR_LOCATION_ROLES },
                locationScopeAware: true,
                matchSubpaths: false,
                requiresBilling: true,
                requiresFeatureFlag: 'alerts',
                nav: {
                    surface: 'topLevel',
                    label: 'Alerts',
                    icon: <WarningAmberIcon />,
                    authRequired: true,
                    roles: STAFF_VIEW_OR_LOCATION_ROLES,
                    matchSubpaths: false
                }
            },
            {
                id: 'alerts-detail',
                title: 'Alert',
                path: 'alerts/:alertId',
                href: '/alerts/:alertId',
                component: AlertDetail,
                guard: 'RoleGuard',
                guardProps: { roles: STAFF_VIEW_OR_LOCATION_ROLES },
                locationScopeAware: true,
                requiresBilling: true,
                requiresFeatureFlag: 'alerts'
            }
        ]
    },
    {
        id: 'waste',
        subheader: 'Waste and Recycling',
        roles: MEMBER_ROLES,
        navSection: {
            id: 'waste',
            label: 'Waste and Recycling',
            icon: <DeleteOutlineIcon />,
            authRequired: true,
            roles: MEMBER_ROLES
        },
        routes: [
            {
                id: 'waste-overview',
                title: 'Waste Overview',
                path: 'waste',
                href: '/waste',
                component: WasteOverview,
                guard: 'RoleGuard',
                guardProps: { roles: MEMBER_ROLES },
                requiresFeatureFlag: 'waste',
                matchSubpaths: false,
                nav: {
                    surface: 'section',
                    label: 'Overview',
                    icon: <DeleteOutlineIcon />,
                    matchSubpaths: false
                }
            }
        ]
    },
    {
        id: 'marketing',
        subheader: 'Marketing',
        roles: SITE_ADMIN_ROLES,
        navSection: {
            id: 'marketing',
            label: 'Marketing',
            icon: <CampaignIcon />,
            pinToTop: true,
            authRequired: true,
            roles: SITE_ADMIN_ROLES
        },
        routes: [
            {
                id: 'marketing-overview',
                title: 'Marketing',
                path: 'marketing',
                href: '/marketing',
                component: MarketingPage,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                matchSubpaths: false,
                nav: {
                    surface: 'section',
                    label: 'Overview',
                    icon: <CampaignIcon />,
                    matchSubpaths: false
                }
            },
            {
                id: 'marketing-organizations',
                title: 'Organizations',
                path: 'marketing/organizations',
                href: '/marketing/organizations',
                component: MarketingOrganizationsPage,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'section',
                    label: 'Organizations',
                    icon: <BusinessIcon />
                }
            },
            {
                id: 'marketing-organization-detail',
                title: 'Organization Details',
                path: 'marketing/organizations/:id',
                href: '/marketing/organizations/:id',
                component: MarketingOrganizationDetailPage,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES }
            },
            {
                id: 'marketing-contacts',
                title: 'Contacts',
                path: 'marketing/contacts',
                href: '/marketing/contacts',
                component: MarketingContactsPage,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'section',
                    label: 'Contacts',
                    icon: <ContactPhoneIcon />
                }
            },
            {
                id: 'marketing-templates',
                title: 'Templates',
                path: 'marketing/templates',
                href: '/marketing/templates',
                component: MarketingTemplatesPage,
                guard: 'RoleGuard',
                guardProps: { roles: SITE_ADMIN_ROLES },
                nav: {
                    surface: 'section',
                    label: 'Templates',
                    icon: <DescriptionOutlinedIcon />
                }
            }
        ]
    }
]

/**
 * Drops the routes marked `fettesHostOnly` unless this is the Fettes host, so neither the route
 * nor its nav entry exists anywhere else. Every consumer reads the filtered `routeConfig`.
 */
export function routesForHost(config, { isFettes }) {
    if (isFettes) return config
    const keep = (route) => !route.fettesHostOnly
    return config.map((section) => ({
        ...section,
        ...(section.items ? { items: section.items.filter(keep) } : {}),
        ...(section.routes ? { routes: section.routes.filter(keep) } : {})
    }))
}

export const routeConfig = routesForHost(ROUTE_CONFIG, {
    isFettes: isFettesHost()
})

export function getRouteEntries(config = routeConfig) {
    return config.flatMap((section) =>
        [...(section.items || []), ...(section.routes || [])].map((route) => ({
            ...route,
            sectionId: section.id,
            sectionSubheader: section.subheader,
            sectionRoles: section.roles,
            sectionNav: section.navSection
        }))
    )
}

export function getRouteHref(route) {
    if (route.href) {
        return route.href
    }

    if (route.index) {
        return '/'
    }

    const path = route.path || ''
    return path.startsWith('/') ? path : `/${path}`
}

function getRoutePattern(route) {
    if (route.index) {
        return '/'
    }

    const path = route.path || ''
    return path.startsWith('/') ? path : `/${path}`
}

function compareRoutesBySpecificity(left, right) {
    return getRoutePattern(right).length - getRoutePattern(left).length
}

export function findDashboardRoute(pathname, config = routeConfig) {
    return getRouteEntries(config)
        .filter((route) => route.component)
        .sort(compareRoutesBySpecificity)
        .find((route) =>
            Boolean(
                matchPath(
                    {
                        path: getRoutePattern(route),
                        end:
                            route.matchSubpaths === false ||
                            route.index === true
                    },
                    pathname
                )
            )
        )
}

export function hasDashboardRoute(pathname, config = routeConfig) {
    return Boolean(findDashboardRoute(pathname, config))
}

export function getDashboardPageTitle(pathname, config = routeConfig) {
    return findDashboardRoute(pathname, config)?.title ?? null
}

export const dashboardCreateOptions = getRouteEntries(routeConfig)
    .filter((route) => route.createAction)
    .map((route) => ({
        ...route.createAction,
        path: getRouteHref(route)
    }))
